<template>
  <div class="image-preview-container">
    <div class="image-list">
      <div
        v-for="(image, index) in modelValue"
        :key="index"
        class="image-item"
      >
        <el-image
          :src="getImageUrl(image)"
          fit="cover"
          :preview-src-list="previewList"
          :initial-index="index"
          :preview-teleported="true"
          class="preview-image"
          style="width: 100px; height: 100px; border-radius: 4px; cursor: pointer"
          crossorigin="anonymous"
          referrerpolicy="no-referrer-when-downgrade"
        >
          <template #error>
            <div class="image-slot">
              <el-icon><Picture /></el-icon>
            </div>
          </template>
        </el-image>
        <!-- 下载按钮覆盖层 -->
        <div class="download-overlay">
          <el-button
            type="primary"
            :icon="Download"
            circle
            size="small"
            class="download-overlay-btn"
            @click.stop="handleDownload(image)"
            title="下载图片"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Download, Picture } from '@element-plus/icons-vue'
import { downloadFileFromServer } from '../utils/fileUtils'
import { getPreviewUrl } from '../api/file'

const props = defineProps({
  // 图片URL数组
  modelValue: {
    type: Array,
    default: () => []
  }
})

// 获取图片预览URL（使用代理接口解决COS 403问题）
const getImageUrl = (url) => {
  if (!url) return url
  return getPreviewUrl(url)
}

// 预览列表（使用代理URL）
const previewList = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) {
    return []
  }
  return props.modelValue.map(url => getImageUrl(url))
})

// 处理遮罩层点击关闭预览
const handleMaskClick = (event) => {
  // 检查点击的是遮罩层而不是图片本身
  const target = event.target
  // 如果点击的是遮罩层（.el-image-viewer__mask）而不是画布区域，则关闭预览
  if (target.classList.contains('el-image-viewer__mask')) {
    // 查找关闭按钮并触发点击
    const closeBtn = document.querySelector('.el-image-viewer__close')
    if (closeBtn) {
      closeBtn.click()
    }
  }
}

// 监听预览器打开/关闭
let observer = null
let maskElement = null

onMounted(() => {
  // 使用 MutationObserver 监听预览器的出现
  observer = new MutationObserver(() => {
    const mask = document.querySelector('.el-image-viewer__mask')
    if (mask && mask !== maskElement) {
      // 预览器已打开，添加点击事件
      maskElement = mask
      mask.addEventListener('click', handleMaskClick)
    } else if (!mask && maskElement) {
      // 预览器已关闭，清理引用
      maskElement = null
    }
  })
  
  // 开始观察 DOM 变化
  observer.observe(document.body, {
    childList: true,
    subtree: true
  })
})

onUnmounted(() => {
  // 清理观察器
  if (observer) {
    observer.disconnect()
  }
  
  // 移除事件监听
  if (maskElement) {
    maskElement.removeEventListener('click', handleMaskClick)
    maskElement = null
  }
})

// 下载图片
// 注意：浏览器缓存30天的设置需要在后端API的响应头中设置 Cache-Control: max-age=2592000
const handleDownload = async (imageUrl) => {
  if (!imageUrl) {
    ElMessage.warning('图片URL为空')
    return
  }

  try {
    // 从URL中提取文件名
    const urlParts = imageUrl.split('/')
    let fileName = urlParts[urlParts.length - 1] || 'image.jpg'
    
    // 移除查询参数
    fileName = fileName.split('?')[0]
    
    // 如果文件名没有扩展名，尝试从URL或Content-Type判断
    if (!fileName.includes('.')) {
      // 尝试从URL路径推断
      const pathParts = imageUrl.split('.')
      if (pathParts.length > 1) {
        const ext = pathParts[pathParts.length - 1].split('?')[0].split('/')[0]
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext.toLowerCase())) {
          fileName = `image.${ext}`
        } else {
          fileName = 'image.jpg'
        }
      } else {
        fileName = 'image.jpg'
      }
    }

    await downloadFileFromServer(imageUrl, fileName)
  } catch (error) {
    console.error('下载图片失败:', error)
    ElMessage.error(error.message || '下载图片失败')
  }
}
</script>

<style scoped>
.image-preview-container {
  width: 100%;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 30px;
}

.download-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 1;
  z-index: 3;
}

.download-overlay-btn {
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

/* 优化预览对话框尺寸（与ImageUpload保持一致） */
:deep(.el-image-viewer__wrapper) {
  .el-image-viewer__canvas {
    img {
      max-width: 60vw;
      max-height: 70vh;
      object-fit: contain;
    }
  }
  
  /* 确保遮罩层可以点击关闭 */
  .el-image-viewer__mask {
    cursor: pointer;
  }
  
  /* 画布区域不触发关闭 */
  .el-image-viewer__canvas {
    cursor: default;
    pointer-events: auto;
  }
}
</style>
